/**
 * Signup attribution handed over from the marketing site.
 *
 * Landing-page signup links append utm_source, utm_medium and utm_campaign to /register (only
 * with the visitor's analytics consent, as the privacy policy describes). Only those three are
 * read; anything else on the URL (click ids, referrers, other utm_* keys) is ignored. They are
 * kept for this browser tab in sessionStorage so the signup can be attributed after the visitor
 * fills in the form or returns from OAuth: `useAuth` sends them with email registration, or to
 * `POST /auth/me/attribution` once a Google sign-in session is known, then clears them.
 */
export const SIGNUP_ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
] as const;

export type SignupAttributionParam = (typeof SIGNUP_ATTRIBUTION_PARAMS)[number];
export type SignupAttribution = Partial<Record<SignupAttributionParam, string>> & {
  /** ISO timestamp of when the parameters were captured. */
  capturedAt?: string;
};

const STORAGE_KEY = 'responsedock.signup-attribution.v1';
const MAX_VALUE_LENGTH = 200;

export function parseSignupAttribution(search: string): Partial<Record<SignupAttributionParam, string>> {
  const params = new URLSearchParams(search);
  const result: Partial<Record<SignupAttributionParam, string>> = {};
  for (const key of SIGNUP_ATTRIBUTION_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) result[key] = value.slice(0, MAX_VALUE_LENGTH);
  }
  return result;
}

/**
 * Stores attribution found on the register page URL. Only `/register` is considered, and a URL
 * without attribution never overwrites what an earlier visit stored.
 */
export function captureSignupAttribution(
  location: Pick<Location, 'pathname' | 'search'> = window.location,
  now: Date = new Date(),
): SignupAttribution | null {
  if (location.pathname.replace(/\/+$/, '') !== '/register') return null;
  const found = parseSignupAttribution(location.search);
  if (Object.keys(found).length === 0) return null;
  const attribution: SignupAttribution = { ...found, capturedAt: now.toISOString() };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable (private mode); attribution is best effort.
  }
  return attribution;
}

export function readSignupAttribution(): SignupAttribution | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: SignupAttribution = {};
    for (const key of [...SIGNUP_ATTRIBUTION_PARAMS, 'capturedAt'] as const) {
      const value = parsed[key];
      if (typeof value === 'string' && value) result[key] = value.slice(0, MAX_VALUE_LENGTH);
    }
    return Object.keys(result).some((key) => key !== 'capturedAt') ? result : null;
  } catch {
    return null;
  }
}

export function clearSignupAttribution(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
